import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { proxyAPI } from '../services/api';

function ProxyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isCreateMode = id === 'create';

  const [loading, setLoading] = useState(!isCreateMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    proxyUrl: '',
    trafficApi: {
      endpoint: '',
      method: 'GET',
      headers: {},
      queryParams: {}
    },
    tags: [],
    isActive: true
  });

  const [headerInput, setHeaderInput] = useState({ key: '', value: '' });
  const [queryParamInput, setQueryParamInput] = useState({ key: '', value: '' });
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    if (!isCreateMode) {
      fetchProxy();
    }
  }, [id]);

  const fetchProxy = async () => {
    try {
      setLoading(true);
      const response = await proxyAPI.getById(id);
      const proxy = response.data;

      // Ensure trafficApi fields are plain objects
      const trafficApi = {
        endpoint: proxy.trafficApi?.endpoint || '',
        method: proxy.trafficApi?.method || 'GET',
        headers: proxy.trafficApi?.headers || {},
        queryParams: proxy.trafficApi?.queryParams || {}
      };

      setFormData({
        ...proxy,
        trafficApi,
        tags: proxy.tags || []
      });
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (isCreateMode) {
        await proxyAPI.create(formData);
      } else {
        await proxyAPI.update(id, formData);
      }
      navigate('/proxies');
    } catch (err) {
      alert('Failed to save proxy: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTrafficApiChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      trafficApi: {
        ...prev.trafficApi,
        [field]: value
      }
    }));
  };

  const addHeader = () => {
    if (headerInput.key && headerInput.value) {
      setFormData(prev => ({
        ...prev,
        trafficApi: {
          ...prev.trafficApi,
          headers: {
            ...prev.trafficApi.headers,
            [headerInput.key]: headerInput.value
          }
        }
      }));
      setHeaderInput({ key: '', value: '' });
    }
  };

  const removeHeader = (key) => {
    const newHeaders = { ...formData.trafficApi.headers };
    delete newHeaders[key];
    handleTrafficApiChange('headers', newHeaders);
  };

  const addQueryParam = () => {
    if (queryParamInput.key && queryParamInput.value) {
      setFormData(prev => ({
        ...prev,
        trafficApi: {
          ...prev.trafficApi,
          queryParams: {
            ...prev.trafficApi.queryParams,
            [queryParamInput.key]: queryParamInput.value
          }
        }
      }));
      setQueryParamInput({ key: '', value: '' });
    }
  };

  const removeQueryParam = (key) => {
    const newParams = { ...formData.trafficApi.queryParams };
    delete newParams[key];
    handleTrafficApiChange('queryParams', newParams);
  };

  const addTag = () => {
    if (tagInput && !formData.tags.includes(tagInput)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <h2>{isCreateMode ? 'Create New Proxy' : 'Edit Proxy'}</h2>

        {error && (
          <div className="alert alert-danger">
            Error: {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Basic Information */}
          <h3>Basic Information</h3>

          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="e.g., Evomi Residential"
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="2"
              placeholder="Optional description"
            />
          </div>

          <div className="form-group">
            <label>Proxy URL *</label>
            <input
              type="text"
              name="proxyUrl"
              value={formData.proxyUrl}
              onChange={handleChange}
              required
              placeholder="https://username:password@host:port"
            />
            <small className="text-muted">
              Format: protocol://username:password@host:port
            </small>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
              />
              {' '}Active
            </label>
          </div>

          {/* Tags */}
          <h3 className="mt-4">Tags</h3>
          <div className="form-group">
            <div className="flex-between">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                placeholder="Add tag"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
              />
              <button type="button" onClick={addTag} className="btn btn-sm btn-secondary">
                Add Tag
              </button>
            </div>
            <div className="mt-2">
              {formData.tags.map(tag => (
                <span key={tag} className="badge badge-secondary mr-1">
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    style={{ marginLeft: '5px', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Traffic API Configuration */}
          <h3 className="mt-4">Traffic API Configuration (Optional)</h3>
          <p className="text-muted">Configure API endpoint to fetch traffic statistics for this proxy</p>

          <div className="form-group">
            <label>API Endpoint</label>
            <input
              type="text"
              value={formData.trafficApi.endpoint}
              onChange={(e) => handleTrafficApiChange('endpoint', e.target.value)}
              placeholder="https://api.provider.com/traffic"
            />
          </div>

          <div className="form-group">
            <label>HTTP Method</label>
            <select
              value={formData.trafficApi.method}
              onChange={(e) => handleTrafficApiChange('method', e.target.value)}
            >
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="DELETE">DELETE</option>
            </select>
          </div>

          {/* Headers */}
          <div className="form-group">
            <label>Headers</label>
            <div className="grid grid-2 mb-2">
              <input
                type="text"
                value={headerInput.key}
                onChange={(e) => setHeaderInput(prev => ({ ...prev, key: e.target.value }))}
                placeholder="Header name (e.g., X-API-KEY)"
              />
              <div className="flex-between">
                <input
                  type="text"
                  value={headerInput.value}
                  onChange={(e) => setHeaderInput(prev => ({ ...prev, value: e.target.value }))}
                  placeholder="Header value"
                  style={{ marginRight: '10px' }}
                />
                <button type="button" onClick={addHeader} className="btn btn-sm btn-secondary">
                  Add
                </button>
              </div>
            </div>
            <div>
              {Object.entries(formData.trafficApi.headers || {}).map(([key, value]) => (
                <div key={key} className="badge badge-info mr-1 mb-1">
                  <code>{key}: {value}</code>
                  <button
                    type="button"
                    onClick={() => removeHeader(key)}
                    style={{ marginLeft: '5px', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Query Parameters */}
          <div className="form-group">
            <label>Query Parameters</label>
            <div className="grid grid-2 mb-2">
              <input
                type="text"
                value={queryParamInput.key}
                onChange={(e) => setQueryParamInput(prev => ({ ...prev, key: e.target.value }))}
                placeholder="Parameter name (e.g., username)"
              />
              <div className="flex-between">
                <input
                  type="text"
                  value={queryParamInput.value}
                  onChange={(e) => setQueryParamInput(prev => ({ ...prev, value: e.target.value }))}
                  placeholder="Parameter value"
                  style={{ marginRight: '10px' }}
                />
                <button type="button" onClick={addQueryParam} className="btn btn-sm btn-secondary">
                  Add
                </button>
              </div>
            </div>
            <div>
              {Object.entries(formData.trafficApi.queryParams || {}).map(([key, value]) => (
                <div key={key} className="badge badge-info mr-1 mb-1">
                  <code>{key}={value}</code>
                  <button
                    type="button"
                    onClick={() => removeQueryParam(key)}
                    style={{ marginLeft: '5px', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex-between mt-4">
            <button
              type="button"
              onClick={() => navigate('/proxies')}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
            >
              {saving ? 'Saving...' : (isCreateMode ? 'Create Proxy' : 'Update Proxy')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ProxyDetail;
